import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, MessageCircleIcon } from 'lucide-react';
import { founder, salon } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { btnPrimaryLg } from '../../ui/styles';
import { closingHeading, heroTitle, quietLink } from '../../ui/sections';
import { ImageSlot } from '../shared/ImageSlot';
import { articleTexture } from '../../data/placeholderImages';
import { useCatalog } from '../../contexts/CatalogContext';
import { NotFound } from '../layout/NotFound';
import { ArticleBlock } from './ArticleBlock';
import { MentionedProducts } from './MentionedProducts';
import { articleNextStep, articlePath, relatedArticles } from './journalRules';

/** Eén journalartikel: kop, tekst, genoemde producten ernaast en "Lees ook". */
export function Article() {
  const {
    slug = ''
  } = useParams();
  const {
    articles,
    articleBySlug,
    productsByIds
  } = useCatalog();
  const article = articleBySlug(slug);
  const navigate = useNavigate();
  // Uit het journal gekomen (met of zonder categorie): één stap terug, met dezelfde keuze en scrollplek.
  const {
    state
  } = useLocation() as {
    state: {
      fromJournal?: string;
    } | null;
  };
  const fromJournal = state?.fromJournal?.startsWith('/journal') ? state.fromJournal : null;
  if (!article) {
    return <NotFound title="Dit artikel bestaat niet (meer)" body="Misschien heeft het een nieuwe titel gekregen. Alle artikelen staan in het journal." backTo={{
      to: '/journal',
      label: 'Naar alle artikelen'
    }} data-id="element-7573" data-mp="2u8oj3mkrrv5d" />;
  }
  const mentioned = productsByIds(article.productIds ?? []);
  const next = articleNextStep(mentioned[0]);
  const related = relatedArticles(articles, article);
  return <>
      <article className="bg-sand" data-id="element-7574" data-mp="1espg0pq0epbz">
        <header className="mx-auto max-w-content px-6 pb-10 pt-10 lg:px-10 lg:pt-14" data-id="element-7575" data-mp="1etimrqoyxeu4">
          <Link to={fromJournal ?? '/journal'} onClick={e => {
          if (!fromJournal) return;
          e.preventDefault();
          navigate(-1);
        }} className="-ml-1 inline-flex min-h-[44px] items-center gap-2 px-1 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-7576" data-mp="1itssd28um38g">
            <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7577" data-mp="2qvygk7f7a8m4" />
            <span data-id="element-7578" data-mp="2qy1ereqib81x">
              <span className="sr-only" data-id="element-7579" data-mp="23645nvefyu4i">Terug naar het </span>Journal
            </span>
          </Link>
          <p className="mt-10 text-[14px] text-clay" data-id="element-7580" data-mp="1y7j5tx33qcgl">
            {article.category} · {article.readTime} lezen · {article.date}
          </p>
          <h1 className={`mt-4 max-w-3xl ${heroTitle}`} data-id="element-7581" data-mp="2c7tzf7vsq55g">{article.title}</h1>
          <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-muted" data-id="element-7582" data-mp="2857abeqieyby">{article.excerpt}</p>
          <div className="mt-8 flex items-center gap-3" data-id="element-7583" data-mp="3nlfv7bw5v4zy">
            <img src={founder.portrait} alt="" width={44} height={44} className="h-11 w-11 shrink-0 rounded-full object-cover object-top" data-id="element-7584" data-mp="3no24ph2m3ukt" />
            <p className="text-[14px] leading-snug text-muted" data-id="element-7585" data-mp="3r3vfi8fhniuj">
              <span className="block text-ink" data-id="element-7586" data-mp="3w382qua9urxn">
                <span className="sr-only" data-id="element-7587" data-mp="3w3zolde2hbaq">Geschreven door </span>
                {founder.name}
              </span>
              {founder.role}
            </p>
          </div>
        </header>

        {/* Het artikelbeeld over de volle breedte; zonder eigen beeld een textuur per categorie (docs/BEELDEN.md, A1).
            Het ene moment: het beeld beweegt licht binnen zijn kader. Sfeerbeeld: de titel staat er al boven. */}
        <ImageSlot code="A1" parallax priority media={{
        image: article.image || articleTexture(article.category),
        alt: ''
      }} sizes="100vw" className="aspect-[4/3] w-full sm:aspect-[21/9]" data-id="element-7588" data-mp="iqeg64j3cdph" />

        <div className="mx-auto grid max-w-content gap-14 px-6 pb-24 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7589" data-mp="wn1pl6s8cwca">
          <div className="max-w-[680px] lg:col-span-7" data-id="element-7590" data-mp="11fl17y7cxheg">
            {article.body.map((block, i) =>
          // Blokken hebben (nog) geen eigen id; de volgorde is vast zolang je leest.
          <ArticleBlock key={i} block={block} data-id="element-7591" data-mp="34vzrhnlw8cln" data-mpi={i} />)}

            {/* Wat doe ik ermee? De hoofdknop gaat naar het eerste genoemde product; de vraag aan Sarah is een rustige link. */}
            <div className="mt-14 border-t border-line pt-8" data-id="element-7592" data-mp="2uzoa4j7g4wkc">
              <Link to={next.to} className={btnPrimaryLg} data-id="element-7593" data-mp="3sm3vhw2zued6">
                {next.label}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7594" data-mp="3nnaevuseu78s" />
              </Link>
              <p className="mt-5 text-[15px] leading-relaxed text-muted" data-id="element-7595" data-mp="3iwal2yel85ai">Twijfel je of dit voor je huid geldt? App me gerust, ik antwoord meestal dezelfde dag.</p>
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-7596" data-mp="3dx7g1dylxz2j">
                <MessageCircleIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7597" data-mp="f9jvejxnfp5y" />
                App {founder.firstName}
                <span className="sr-only" data-id="element-7598" data-mp="iq6dqehb66yv">{shopLabels.viaWhatsApp}</span>
              </a>
            </div>
          </div>

          {mentioned.length > 0 && <aside className="lg:col-span-4 lg:col-start-9" aria-labelledby="genoemd" data-id="element-7599" data-mp="mst6qipptxxn">
              <div className="bg-shell p-6 lg:sticky lg:top-32" data-id="element-7600" data-mp="rs5v57w8jd2c">
                <h2 id="genoemd" className="type-name text-ink" data-id="element-7601" data-mp="wpz05xlsy111">
                  Genoemd in dit artikel
                </h2>
                <div className="mt-1" data-id="element-7602" data-mp="11m67nutujkl3">
                  <MentionedProducts products={mentioned} variant="sidebar" data-id="element-7603" data-mp="15pc9mq9xeplr" />
                </div>
              </div>
            </aside>}
        </div>
      </article>

      {related.length > 0 && <section className="border-t border-line bg-shell py-16 lg:py-20" aria-labelledby="lees-ook" data-id="element-7604" data-mp="1ffx5i5y7tg0z">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7605" data-mp="1kfjl2m1in8cy">
            <h2 id="lees-ook" className={`${closingHeading} text-ink`} data-id="element-7606" data-mp="1pdutcsc5xat2">
              Lees ook
            </h2>
            <ul className="mt-8 grid gap-8 md:grid-cols-2" data-id="element-7607" data-mp="zpfw0uj4utko">
              {related.map(a => <li key={a.slug} data-id="element-7608" data-mp="uquxr0nfyjyv">
                  <Link to={articlePath(a)} className="group block border-t border-ink/15 pt-6" data-id="element-7609" data-mp="urxlx0tdq8b3">
                    <p className="text-[13px] text-clay" data-id="element-7610" data-mp="19gals8vl8bav">
                      {a.category} · {a.readTime}
                    </p>
                    <h3 className="mt-2 type-title text-ink transition-colors duration-150 ease-soft group-hover:text-clay" data-id="element-7611" data-mp="14mplga6iv58i">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-7612" data-mp="1dgko9bk68jkz">{a.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-[14px] text-ink" aria-hidden="true" data-id="element-7613" data-mp="1sduzqkwtdpgp">
                      Lees verder
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} data-id="element-7614" data-mp="3u5gfkwbcgslb" />
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}
    </>;
}